import { useMutation, useQuery } from "@apollo/client/react";
import { GQL } from "@binale-tech/shared";
import React, { type FC, memo, use, useCallback, useMemo, useState } from "react";
import { useToastSaver } from "@ui-components/hooks/useToastSaver";

import { FormattedMessage } from "react-intl";
import { Accordion, Button, Checkbox, cn, Drawer, Label, Separator, Spinner } from "@heroui/react";
import { useSaldoTransferInput } from "@domain/context/accountingData/useSaldoTransferInput";
import { companyTransferSaldo, companyTransferSaldoLogs } from "@domain/graphql/companySaldo";

import { ArrowRight } from "@gravity-ui/icons";
import { UserContext } from "@domain/context/UserProvider";
import { ProductAccessUtils } from "@domain/models/utils/ProductAccessUtils";

const CompanySaldoDrawerImpl: FC<{
    year: number;
    onClose: () => void;
    companyData: GQL.ICompany;
    /** The company with members, which companyData (the year form query) does not carry. */
    accessCompany: GQL.ICompany | undefined;
}> = ({ year: yearTo, onClose, companyData, accessCompany }) => {
    const user = use(UserContext);
    const toastSaver = useToastSaver();
    const [transferSaldo] = useMutation(companyTransferSaldo);
    const companyId = companyData?.id;
    const open = Boolean(yearTo);

    const [loading, setLoading] = useState(false);
    const [modules, setModules] = useState<Set<GQL.IProductKey>>(new Set());
    const input = useSaldoTransferInput(yearTo, modules, companyData);

    const { loading: logsLoading, data: logsData } = useQuery(companyTransferSaldoLogs, {
        variables: { companyId, yearTo },
        fetchPolicy: "no-cache",
        skip: !companyId || !yearTo,
    });

    const canWriteProduct = (pk: GQL.IProductKey) => ProductAccessUtils.canWrite(pk, accessCompany, user);
    const { canSubmit, missingFe } = useMemo(
        () => ProductAccessUtils.getSaldoTransferAccess(input, accessCompany, user),
        [input, accessCompany, user],
    );

    const handleTransferSaldo = useCallback(() => {
        setLoading(true);
        toastSaver(() => transferSaldo({ variables: { input } })).finally(() => setLoading(false));
    }, [toastSaver, transferSaldo, input]);
    const onDrawerOpenChange = useCallback((isOpen: boolean) => !isOpen && onClose(), [onClose]);

    if (!companyId) {
        return null;
    }

    const onValueChange = (pk: GQL.IProductKey) => (isSelected: boolean) => {
        setModules((prevState) => {
            if (isSelected) {
                prevState.add(pk);
            } else {
                prevState.delete(pk);
            }
            return new Set(prevState);
        });
    };

    return (
        <Drawer.Backdrop isOpen={open} onOpenChange={onDrawerOpenChange}>
            <Drawer.Content placement="right">
                <Drawer.Dialog className="w-250 max-w-250">
                    <Drawer.CloseTrigger />
                    <Drawer.Header>
                        <Drawer.Heading>
                            <FormattedMessage id="app.companies.saldoTransfer" />: {yearTo - 1}&nbsp;
                            <ArrowRight className="size-4" />
                            &nbsp;{yearTo}
                        </Drawer.Heading>
                    </Drawer.Header>
                    <Drawer.Body>
                        <div className="flex flex-col gap-2 w-full">
                            <Checkbox
                                isDisabled={loading || !canWriteProduct(GQL.IProductKey.Er)}
                                isSelected={modules.has(GQL.IProductKey.Er)}
                                onChange={onValueChange(GQL.IProductKey.Er)}
                            >
                                <Checkbox.Content>
                                    <Checkbox.Control
                                        className={cn({
                                            "border border-border-tertiary": !modules.has(GQL.IProductKey.Er),
                                        })}
                                    >
                                        <Checkbox.Indicator />
                                    </Checkbox.Control>
                                    <Label>
                                        <FormattedMessage id="app.titles.ER" />
                                    </Label>
                                </Checkbox.Content>
                            </Checkbox>
                            <Checkbox
                                isDisabled={loading || !canWriteProduct(GQL.IProductKey.ErA)}
                                isSelected={modules.has(GQL.IProductKey.ErA)}
                                onChange={onValueChange(GQL.IProductKey.ErA)}
                            >
                                <Checkbox.Content>
                                    <Checkbox.Control
                                        className={cn({
                                            "border border-border-tertiary": !modules.has(GQL.IProductKey.ErA),
                                        })}
                                    >
                                        <Checkbox.Indicator />
                                    </Checkbox.Control>
                                    <Label>
                                        <FormattedMessage id="app.titles.ER_A" />
                                    </Label>
                                </Checkbox.Content>
                            </Checkbox>
                            <Checkbox
                                isDisabled={loading || !canWriteProduct(GQL.IProductKey.Deb)}
                                isSelected={modules.has(GQL.IProductKey.Deb)}
                                onChange={onValueChange(GQL.IProductKey.Deb)}
                            >
                                <Checkbox.Content>
                                    <Checkbox.Control
                                        className={cn({
                                            "border border-border-tertiary": !modules.has(GQL.IProductKey.Deb),
                                        })}
                                    >
                                        <Checkbox.Indicator />
                                    </Checkbox.Control>
                                    <Label>
                                        <FormattedMessage id="app.titles.Deb" />
                                    </Label>
                                </Checkbox.Content>
                            </Checkbox>
                            <Checkbox
                                isDisabled={loading || !canWriteProduct(GQL.IProductKey.Bank)}
                                isSelected={modules.has(GQL.IProductKey.Bank)}
                                onChange={onValueChange(GQL.IProductKey.Bank)}
                            >
                                <Checkbox.Content>
                                    <Checkbox.Control
                                        className={cn({
                                            "border border-border-tertiary": !modules.has(GQL.IProductKey.Bank),
                                        })}
                                    >
                                        <Checkbox.Indicator />
                                    </Checkbox.Control>
                                    <Label>
                                        <FormattedMessage id="app.titles.Bank.pl" />
                                    </Label>
                                </Checkbox.Content>
                            </Checkbox>
                            <Checkbox
                                isDisabled={loading || !canWriteProduct(GQL.IProductKey.Kb)}
                                isSelected={modules.has(GQL.IProductKey.Kb)}
                                onChange={onValueChange(GQL.IProductKey.Kb)}
                            >
                                <Checkbox.Content>
                                    <Checkbox.Control
                                        className={cn({
                                            "border border-border-tertiary": !modules.has(GQL.IProductKey.Kb),
                                        })}
                                    >
                                        <Checkbox.Indicator />
                                    </Checkbox.Control>
                                    <Label>
                                        <FormattedMessage id="app.titles.KB.pl" />
                                    </Label>
                                </Checkbox.Content>
                            </Checkbox>

                            <Separator className="mt-[50px]" />

                            <div className="flex gap-2 h-8">
                                <strong>
                                    <FormattedMessage id="app.companies.logs" /> {yearTo}
                                </strong>
                                {logsLoading && (
                                    <Button isPending={logsLoading} variant="secondary" isIconOnly>
                                        {({ isPending }) => (isPending ? <Spinner color="current" size="sm" /> : null)}
                                    </Button>
                                )}
                            </div>
                            <Accordion variant="surface">
                                {logsData?.companyTransferSaldoLogs?.map((log) => (
                                    <Accordion.Item key={log.id} id={log.id}>
                                        <Accordion.Heading>
                                            <Accordion.Trigger>
                                                <div className="flex flex-col">
                                                    <span>{log.createdAt}</span>
                                                    <span className="text-sm text-muted">{log.userId}</span>
                                                </div>
                                                <Accordion.Indicator />
                                            </Accordion.Trigger>
                                        </Accordion.Heading>
                                        <Accordion.Panel>
                                            <Accordion.Body>
                                                <pre>{JSON.stringify(log, null, 2)}</pre>
                                            </Accordion.Body>
                                        </Accordion.Panel>
                                    </Accordion.Item>
                                ))}
                            </Accordion>
                        </div>
                    </Drawer.Body>
                    <Drawer.Footer>
                        {missingFe && (
                            <p className="text-sm text-danger" data-testid="saldo-fe-write-hint">
                                <FormattedMessage id="app.companies.saldoTransfer.feWriteRequired" />
                            </p>
                        )}
                        <Button
                            size="lg"
                            isDisabled={modules.size === 0 || !canSubmit}
                            isPending={loading}
                            onPress={handleTransferSaldo}
                        >
                            {({ isPending }) => (
                                <>
                                    {isPending ? <Spinner color="current" size="sm" /> : null}
                                    <FormattedMessage id="app.companies.go" />
                                </>
                            )}
                        </Button>
                    </Drawer.Footer>
                </Drawer.Dialog>
            </Drawer.Content>
        </Drawer.Backdrop>
    );
};

export const CompanySaldoDrawer = memo(CompanySaldoDrawerImpl);
